import {
  ApolloClient,
  HttpOptions,
  InMemoryCache,
  NormalizedCacheObject,
  createHttpLink,
  split,
} from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { RetryLink } from '@apollo/client/link/retry';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { getMainDefinition } from '@apollo/client/utilities';
import { CachePersistor, LocalForageWrapper } from 'apollo3-cache-persist';
import { createClient } from 'graphql-ws';
import localForage from 'localforage';
import { clamp } from 'lodash';
import { isLocalDev } from '../config/config';
import { sleep } from '../util/async';
import { logger } from '../util/logger';
import { getPlatform } from '../util/platform';
import { VERSION } from '../version';

export type HeaderProvider = () => Promise<Record<string, string>>;
export type CacheProvider = () => Promise<{
  cacheName?: string;
  cache: InMemoryCache;
}>;

const createApolloClient = async (options: {
  url: string;
  cacheProvider: CacheProvider;
  headerProvider: HeaderProvider;
}): Promise<ApolloClient<NormalizedCacheObject>> => {
  const { url, cacheProvider, headerProvider } = options;

  const commonHttpLinkOptions: HttpOptions = {
    uri: url,
    credentials: 'include',
  };

  const httpLink = createHttpLink(commonHttpLinkOptions);

  const authLink = setContext(async (_, { headers }) => {
    const extraHeaders = await headerProvider();

    return {
      headers: {
        ...headers,
        ...extraHeaders,
      },
    };
  });

  const retryLink = new RetryLink({
    attempts: {
      max: 3,
    },
    delay: {
      initial: 500,
      jitter: true,
    },
  });

  const webSocketLink = createWebSocketLink({
    url,
    headerProvider,
  });

  // using the ability to split links, you can send data to each link
  // depending on what kind of operation is being sent
  const link = split(
    // split based on operation type
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === 'OperationDefinition' &&
        definition.operation === 'subscription'
      );
    },
    webSocketLink,
    authLink.concat(retryLink).concat(httpLink)
  );

  const { cacheName, cache } = await cacheProvider();

  try {
    const localForageInstance = localForage.createInstance({
      name: cacheName,
    });

    const persistor = new CachePersistor<any>({
      cache,
      storage: new LocalForageWrapper(localForageInstance),
    });

    await persistor.restore();
  } catch (error) {
    logger.error('error restoring Apollo cache', { error });
  }

  const client = new ApolloClient({
    cache,
    link,
    name: getPlatform(),
    version: VERSION,
    connectToDevTools: isLocalDev(),
  });

  setApolloWindowClient(client);

  return client;
};

/**
 * Set the Apollo client to allow us to access it from Apollo DevTools.
 */
const setApolloWindowClient = (client: ApolloClient<NormalizedCacheObject>) => {
  if (typeof window !== 'undefined') {
    window.__APOLLO_CLIENT__ = client;
  }
};

const createWebSocketLink = (options: {
  url: string;
  headerProvider: HeaderProvider;
}): GraphQLWsLink => {
  const { headerProvider } = options;

  const url = options.url
    .replace('http://', 'ws://')
    .replace('https://', 'wss://');

  // When attempting to reconnect, these determine the
  // backoff increment and max delay in milliseconds.
  const reconnectDelayIncrement = 2_000;
  const reconnectMaxDelay = 10_000;

  // The delay to use for the next retry.
  // Starts at the initial delay increment and increases by the increment
  // until the max delay is reached. Since this value is recalculated in a
  // separate function than those that need to reference it, we store it here.
  let reconnectDelay = reconnectDelayIncrement;

  // If stop receiving messages from the server for this amount of time,
  // then terminate the socket and reconnect.
  let terminationTimeout: NodeJS.Timeout;

  /**
   * Sets a timeout that unless cleared will terminate the socket.
   * Since the termination is non-fatal, this triggers a reconnect.
   */
  const setSocketTerminationTimeout = (options: {
    /**
     * Calculate the timeout delay in milliseconds,
     * after which the socket will be terminated with the given message.
     */
    delay: () => number;
    /**
     * Message to log when the socket is terminated.
     */
    message: string;
  }) => {
    const { delay, message } = options;
    const timeout = delay();

    cancelSocketTerminationTimeout();

    logger.debug(`setting socket termination for ${timeout}ms`);

    terminationTimeout = setTimeout(() => {
      logger.warn(`terminating socket client after timeout: ${message}`);
      // Terminate the socket which will trigger a reconnect.
      // https://github.com/enisdenjo/graphql-ws/discussions/290
      wsLink.client.terminate();
    }, timeout);
  };

  const cancelSocketTerminationTimeout = () => {
    clearTimeout(terminationTimeout);
  };

  const wsLink = new GraphQLWsLink(
    createClient({
      url,

      /**
       * Returns authorization headers needed to authenticate to server
       */
      connectionParams: async () => {
        const headers = await headerProvider();

        return {
          ...headers,
        };
      },

      /**
       * Auto establish connection with server versus waiting for a
       * subscription
       */
      lazy: true,

      /**
       * With keep-alive set, then the client will play ping-pong
       * with the server to keep the web socket open.
       *
       * Use the `ping` and `pong` event callbacks to monitor how long
       * it takes for the server to respond to identify timeouts.
       */
      keepAlive: 15_000,

      on: {
        /**
         * If received is false then the ping was sent from the client.
         * https://the-guild.dev/graphql/ws/docs/client/type-aliases/EventPingListener
         */
        ping: (received: boolean) => {
          logger.debug('socket ping', { received });
          if (!received) {
            // Client sent ping to the server. Start timer waiting for pong.
            setSocketTerminationTimeout({
              delay: () => 10_000,
              message: 'pong not received from server',
            });
          }
        },
        /**
         * If received is true then the pong was received from the server.
         * https://the-guild.dev/graphql/ws/docs/client/type-aliases/EventPongListener
         */
        pong: (received: boolean) => {
          logger.debug('socket pong', { received });
          if (received) {
            // Pong received from the server. Cancel the timeout.
            cancelSocketTerminationTimeout();
          }
        },
        /**
         * Lifecycle is CONNECTING => OPENED => CONNECTED => CLOSED.
         * If isRetry is true then the connection is being retried.
         * https://the-guild.dev/graphql/ws/docs/client/type-aliases/EventConnectingListener
         */
        connecting: (isRetry: boolean) => {
          logger.info('socket connecting', { isRetry });
          setSocketTerminationTimeout({
            delay: () => reconnectDelay,
            message: 'hung on connecting',
          });
        },
        /**
         * Lifecycle is CONNECTING => OPENED => CONNECTED => CLOSED.
         * Socket is a WebSocket instance, but because the library can be
         * used in browser and nodejs, the library doesn't specify the type.
         * https://the-guild.dev/graphql/ws/docs/client/type-aliases/EventOpenedListener
         */
        opened: (_socket: unknown) => {
          logger.info('socket opened');
          setSocketTerminationTimeout({
            delay: () => reconnectDelay,
            message: 'hung on opened',
          });
        },
        /**
         * Lifecycle is CONNECTING => OPENED => CONNECTED => CLOSED.
         * Socket is a WebSocket instance, but because the library can be
         * used in browser and nodejs, the library doesn't specify the type.
         * https://the-guild.dev/graphql/ws/docs/client/type-aliases/EventConnectedListener
         */
        connected: (_socket: unknown) => {
          logger.info('socket connected');
          cancelSocketTerminationTimeout();
        },
        /**
         * Lifecycle is CONNECTING => OPENED => CONNECTED => CLOSED.
         * https://the-guild.dev/graphql/ws/docs/client/type-aliases/EventClosedListener
         */
        closed: (errorOrEvent) => {
          logger.warn('socket closed', { errorOrEvent });
          cancelSocketTerminationTimeout();
        },
        /**
         * Handle errors that occur on the socket.
         * https://the-guild.dev/graphql/ws/docs/client/type-aliases/EventErrorListener
         */
        error: (errorOrEvent) => {
          logger.warn('socket error', { errorOrEvent });
          cancelSocketTerminationTimeout();
        },
      },

      /**
       * When the socket closes or fails due to a non-fatal reason,
       * should it retry until the max retry attempts is reached?
       */
      shouldRetry: () => true,

      /**
       * How many times should the socket retry before giving up?
       */
      retryAttempts: Infinity,

      /**
       * Custom retry logic that implements a bounded backoff.
       */
      retryWait: async (retries: number): Promise<void> => {
        reconnectDelay = clamp(
          reconnectDelayIncrement * retries,
          reconnectDelayIncrement,
          reconnectMaxDelay
        );

        logger.info('waiting to reconnect websocket', {
          retries,
          reconnectDelay,
        });

        await sleep(reconnectDelay);
      },
    })
  );

  return wsLink;
};

export { createApolloClient };
